/* eslint-disable no-shadow */
import {useReducer} from 'react';

import {CURRENT_SLIDE_INDEX_ACTION_PROP} from '../../../enums/lessons';
import {actionTypes} from './reducerState';

const getActionsToIgnore = () => [
  actionTypes.SET_ACTIVE_SLIDE,
  actionTypes.SET_ACTIVE_SLIDE_ELEMENT,
  actionTypes.MOVE_TO_NEXT_SLIDE_ELEMENT,
  actionTypes.MOVE_TO_PREVIOUS_SLIDE_ELEMENT,
];

const mergeHistory = (states) =>
  states.filter((state, i) => {
    if (i + 1 === states.length) {
      return true;
    }

    const hasDifferentActionTypeToNextState =
      state.action.type !== states[i + 1].action.type;

    const getActionPayloadKeys = (state) => {
      const keys = Object.keys(state.action?.payload ?? {});
      return keys.filter((key) => key !== CURRENT_SLIDE_INDEX_ACTION_PROP);
    };
    const actionPayloadKeys = getActionPayloadKeys(state);
    const nextStatesActionPayloadKeys = getActionPayloadKeys(states[i + 1]);

    const hasActionPayloadOfMultipleFields =
      actionPayloadKeys.length > 1 || nextStatesActionPayloadKeys.length > 1;

    const hasDifferentActionPayloadToNextState =
      actionPayloadKeys[0] !== nextStatesActionPayloadKeys[0];

    const hasImageChangeInActionPayload =
      actionPayloadKeys.includes('image') ||
      nextStatesActionPayloadKeys.includes('image');

    return (
      hasDifferentActionTypeToNextState ||
      hasActionPayloadOfMultipleFields ||
      hasDifferentActionPayloadToNextState ||
      hasImageChangeInActionPayload
    );
  });

export const getHasOnlyActiveSlideChanges = (stateHistory) =>
  stateHistory.every((item) =>
    getActionsToIgnore().includes(item?.action?.type)
  );

const rewriteUnsavedChanges = (state) => {
  const hasUndoneASave = state.future.some(
    (f) => f.action?.type === actionTypes.CHANGES_SUBMITTED
  );

  if (hasUndoneASave) {
    state.present.unsavedChanges = true;
  }

  return state;
};

const useUndoReducer = (reducer, initialState) => {
  const undoState = {
    past: [],
    present: initialState,
    future: [],
  };

  // eslint-disable-next-line complexity, max-statements
  const undoReducer = (state, action) => {
    const newPresent = reducer(state.present, action);
    let previousAction;

    const updateState = (newState) => {
      previousAction = action;
      return rewriteUnsavedChanges(newState);
    };

    if (action.type === useUndoReducer.types.undo) {
      const {past: currentPast} = state;
      if (currentPast.length < 1 || getHasOnlyActiveSlideChanges(currentPast)) {
        return state;
      }

      let [newPresent, ...newPast] = currentPast;
      let newFuture = [];
      for (let i = 0; i < currentPast.length; i++) {
        const {type} = currentPast[i].action;
        if (
          ![
            ...getActionsToIgnore(),
            actionTypes.CHANGES_SUBMITTED,
            actionTypes.HIDE_DELETE_QUESTION_WARNING,
            actionTypes.DELETE_QUESTION,
          ].includes(type)
        ) {
          const index =
            currentPast[i + 1]?.action?.type ===
            (actionTypes.SHOW_DELETE_WARNING ||
              actionTypes.SHOW_DELETE_QUESTION_WARNING)
              ? i + 1
              : i;

          newPresent = currentPast[index];
          newPast = currentPast.slice(index + 1);
          newFuture = currentPast.slice(0, i);
          break;
        }
      }

      return updateState({
        past: newPast,
        present: newPresent,
        future: [...newFuture, state.present, ...state.future],
      });
    }

    if (action.type === useUndoReducer.types.redo) {
      const {future: currentFuture} = state;

      if (
        currentFuture.length < 1 ||
        getHasOnlyActiveSlideChanges(currentFuture)
      ) {
        return state;
      }

      let [newPresent, ...newFuture] = currentFuture;
      let newPast;
      for (let i = 0; i < currentFuture.length; i++) {
        if (currentFuture[i].action?.type !== actionTypes.CHANGES_SUBMITTED) {
          newPresent = currentFuture[i];
          newPast = currentFuture.slice(0, i);
          newFuture = currentFuture.slice(i + 1);
          break;
        }
      }

      return updateState({
        past: [...newPast, state.present, ...state.past],
        present: newPresent,
        future: newFuture,
      });
    }

    // Allow redo after changing slide, assuming no other changes have been made
    if (
      action.type === actionTypes.SET_ACTIVE_SLIDE ||
      action.type === actionTypes.CHANGES_SUBMITTED ||
      previousAction?.type === useUndoReducer.types.undo
    ) {
      previousAction = action;
      return {
        past: mergeHistory([{...state.present, action}, ...state.past]),
        present: newPresent,
        future: state.future,
      };
    }

    previousAction = action;
    return {
      past: mergeHistory([{...state.present, action}, ...state.past]),
      present: newPresent,
      future: [],
    };
  };

  return useReducer(undoReducer, undoState);
};

useUndoReducer.types = {
  undo: 'UNDO',
  redo: 'REDO',
};

export default useUndoReducer;
